import { brandStorage as localStorage } from '@/utils/brandSession';
import React, { useState } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Eye, EyeOff, Mail, Shield, Info, Smartphone } from 'lucide-react';
import axios from 'axios';
import { API } from '../utils/config';
import { formatApiErrorDetail } from '../utils/errorDetail';
import { homeSmartEntryPath, saveSiteSession } from '../utils/brandSession';
import { assertSessionStorage } from '../utils/safeStorage';

const LoginPage = ({ brand = 'hw-smart' }) => {
  const isHomeSmart = brand === 'home-smart';
  
  // Load saved credentials
  const savedEmailKey = isHomeSmart ? 'home_smart_saved_email' : 'hw_saved_email';
  const rememberKey = isHomeSmart ? 'home_smart_remember_email' : 'hw_remember_me';
  const savedEmail = localStorage.getItem(savedEmailKey) || '';
  const rememberMe = localStorage.getItem(rememberKey) === 'true';
  
  const [email, setEmail] = useState(savedEmail);
  const [password, setPassword] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [savePassword, setSavePassword] = useState(rememberMe);
  const [show2FAInput, setShow2FAInput] = useState(false);
  const [pendingEmail, setPendingEmail] = useState('');
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetSuccess, setResetSuccess] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);
  const [resetError, setResetError] = useState('');

  React.useEffect(() => {
    localStorage.removeItem('hw_saved_password');
  }, []);

  React.useEffect(() => {
    document.title = isHomeSmart ? 'Home Smart · Accesso' : 'HWsmart · Accesso';
  }, [isHomeSmart]);

  const persistRememberedEmail = () => {
    localStorage.removeItem('hw_saved_password');
    if (savePassword) {
      localStorage.setItem(savedEmailKey, email);
      localStorage.setItem(rememberKey, 'true');
    } else {
      localStorage.removeItem(savedEmailKey);
      localStorage.removeItem(rememberKey);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      assertSessionStorage();
      const response = await axios.post(`${API}/api/auth/login`, {
        email,
        password
      }, {params: {brand: isHomeSmart ? 'home-smart' : undefined}});

      if (isHomeSmart && !response.data.user?.is_external_tenant && response.data.token_type !== '2fa_pending') {
        setError('Accesso non disponibile. Verifica le credenziali.');
        setLoading(false);
        return;
      }

      // Check if 2FA is required
      if (response.data.token_type === '2fa_pending') {
        setShow2FAInput(true);
        setPendingEmail(email);
        setLoading(false);
        return;
      }

      // Normal login without 2FA
      saveSiteSession(response.data.access_token, response.data.user);
      if (response.data.user?.is_external_tenant) localStorage.setItem('home_smart_context', 'true');
      else localStorage.removeItem('home_smart_context');
      
      persistRememberedEmail();
      
      // Redirect based on role
      const userRole = response.data.user?.role;
      if (isHomeSmart || response.data.user?.is_external_tenant) {
        window.location.href = homeSmartEntryPath();
      } else if (userRole === 'property_responsible') {
        window.location.href = '/responsabile/dashboard';
      } else {
        window.location.href = '/';
      }
    } catch (err) {
      setError(err.code === 'STORAGE_UNAVAILABLE' ? err.message : formatApiErrorDetail(err.response?.data?.detail, 'Errore durante il login'));
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setResetLoading(true);
    setResetError('');
    setResetSuccess(false);

    try {
      await axios.post(`${API}/api/auth/forgot-password`, {
        email: resetEmail
      }, {params: {brand: isHomeSmart ? 'home-smart' : undefined}});
      
      setResetSuccess(true);
      setTimeout(() => {
        setShowForgotPassword(false);
        setResetSuccess(false);
        setResetEmail('');
      }, 3000);
    } catch (err) {
      setResetError(err.response?.data?.detail || 'Errore durante la richiesta');
    } finally {
      setResetLoading(false);
    }
  };

  const handleVerify2FA = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      assertSessionStorage();
      const response = await axios.post(`${API}/api/auth/2fa/verify`, {
        email: pendingEmail,
        otp_code: otpCode
      }, {params: {brand: isHomeSmart ? 'home-smart' : undefined}});

      if (isHomeSmart && !response.data.user?.is_external_tenant) {
        setError('Accesso non disponibile. Verifica le credenziali.');
        setLoading(false);
        return;
      }

      saveSiteSession(response.data.access_token, response.data.user);
      if (response.data.user?.is_external_tenant) localStorage.setItem('home_smart_context', 'true');
      else localStorage.removeItem('home_smart_context');
      
      persistRememberedEmail();
      
      window.location.href = isHomeSmart || response.data.user?.is_external_tenant ? homeSmartEntryPath() : '/';
    } catch (err) {
      setError(err.code === 'STORAGE_UNAVAILABLE' ? err.message : formatApiErrorDetail(err.response?.data?.detail, 'Codice non valido'));
      setLoading(false);
    }
  };

  const handleBack = () => {
    setShow2FAInput(false);
    setOtpCode('');
    setError('');
  };

  return (
    <div className={`min-h-screen flex items-center justify-center p-4 ${isHomeSmart ? 'bg-[#eef4fc]' : 'bg-gradient-to-br from-blue-50 via-white to-slate-50'}`} data-testid={isHomeSmart ? 'home-smart-login-page' : 'hw-smart-login-page'}>
      <Card className={`w-full max-w-md p-8 shadow-2xl ${isHomeSmart ? 'border-[#c7d5e8]' : ''}`}>
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <img src={isHomeSmart ? '/home-smart-logo.png' : '/1.png'} alt={isHomeSmart ? 'Home Smart' : 'HW Smart'} className={isHomeSmart ? 'h-24 w-24 rounded-full object-contain' : 'h-20 w-auto'} data-testid="login-brand-logo" />
          </div>
          <h1 className="text-3xl font-bold text-slate-800 mb-2" data-testid="login-title">
            {show2FAInput ? 'Verifica Identità' : (isHomeSmart ? 'Bentornato' : 'Benvenuto')}
          </h1>
          <p className="text-slate-600" data-testid="login-subtitle">
            {show2FAInput 
              ? 'Inserisci il codice dalla tua app di autenticazione' 
              : (isHomeSmart ? 'Accedi a Home Smart' : 'Accedi a HWsmart')}
          </p>
        </div>

        {!show2FAInput ? (
          // Normal login form
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="email" className="text-slate-700 font-medium">
                Email
              </Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="email@esempio.com"
                required
                disabled={loading}
                className="mt-1"
                data-testid="login-email-input"
              />
            </div>

            <div>
              <Label htmlFor="password" className="text-slate-700 font-medium">
                Password
              </Label>
              <div className="relative mt-1">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  disabled={loading}
                  className="pr-10"
                  data-testid="login-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-700"
                  data-testid="login-password-visibility-button"
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="savePassword"
                  checked={savePassword}
                  onChange={(e) => setSavePassword(e.target.checked)}
                  className="h-4 w-4 text-blue-600 rounded"
                  data-testid="login-remember-checkbox"
                />
                <Label htmlFor="savePassword" className="ml-2 text-sm text-slate-600">
                  Ricorda email
                </Label>
              </div>
              
              <button
                type="button"
                onClick={() => setShowForgotPassword(true)}
                className="text-sm text-blue-600 hover:text-blue-800 hover:underline"
                data-testid="login-forgot-password-button"
              >
                Password dimenticata?
              </button>
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm" data-testid="login-error-alert">
                {error}
              </div>
            )}

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-gradient-to-r from-blue-900 to-blue-800 hover:from-blue-800 hover:to-blue-700 text-white py-3 text-lg"
              data-testid="login-submit-button"
            >
              {loading ? 'Accesso in corso...' : 'Accedi'}
            </Button>
          </form>
        ) : (
          // 2FA verification form
          <form onSubmit={handleVerify2FA} className="space-y-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
              <div className="flex items-start gap-3">
                <Shield className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
                <div className="text-sm text-blue-800">
                  <p className="font-medium mb-1">Autenticazione a Due Fattori Attiva</p>
                  <p className="text-blue-700">
                    Apri la tua app di autenticazione (Google Authenticator, Authy, ecc.) e inserisci il codice di 6 cifre.
                  </p>
                </div>
              </div>
            </div>

            <div>
              <Label htmlFor="otp" className="text-slate-700 font-medium flex items-center gap-2">
                <Smartphone className="h-4 w-4" />
                Codice di Verifica
              </Label>
              <Input
                id="otp"
                type="text"
                value={otpCode}
                onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="123456"
                required
                disabled={loading}
                className="mt-1 text-center text-2xl tracking-widest font-mono"
                autoFocus
                maxLength={6}
                data-testid="login-otp-input"
              />
              <p className="text-xs text-slate-500 mt-1">
                Il codice cambia ogni 30 secondi
              </p>
            </div>

            {error && (
              <div role="alert" data-testid="login-otp-error-alert" className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                {error}
              </div>
            )}

            <div className="flex gap-3">
              <Button
                type="button"
                onClick={handleBack}
                variant="outline"
                disabled={loading}
                className="flex-1"
                data-testid="login-2fa-back-button"
              >
                Indietro
              </Button>
              <Button
                type="submit"
                disabled={loading || otpCode.length !== 6}
                className="flex-1 bg-gradient-to-r from-blue-900 to-blue-800 hover:from-blue-800 hover:to-blue-700"
                data-testid="login-2fa-submit-button"
              >
                {loading ? 'Verifica...' : 'Verifica'}
              </Button>
            </div>
          </form>
        )}

        {/* 2FA Explanation */}
        {!show2FAInput && !isHomeSmart && (
          <div className="mt-6 pt-6 border-t border-slate-200" data-testid="login-2fa-information">
            <div className="bg-slate-50 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <Info className="h-5 w-5 text-slate-600 mt-0.5 flex-shrink-0" />
                <div className="text-sm text-slate-600">
                  <p className="font-medium mb-2">🔒 Sicurezza Avanzata</p>
                  <p className="mb-2">
                    Gli account amministrativi sono protetti con <strong>Autenticazione a Due Fattori (2FA)</strong>.
                  </p>
                  <p className="text-xs">
                    Dopo aver inserito email e password, ti verrà richiesto un codice dalla tua app di autenticazione (Google Authenticator, Microsoft Authenticator, Authy).
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </Card>

      {/* Forgot Password Modal */}
      <Dialog open={showForgotPassword} onOpenChange={setShowForgotPassword}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5 text-blue-600" />
              Recupera Password
            </DialogTitle>
          </DialogHeader>
          
          {!resetSuccess ? (
            <form onSubmit={handleForgotPassword} className="space-y-4">
              <div>
                <p className="text-sm text-slate-600 mb-4">
                  Inserisci il tuo indirizzo email e ti invieremo un link per reimpostare la password.
                </p>
                
                <Label htmlFor="resetEmail" className="text-slate-700 font-medium">
                  Email
                </Label>
                <Input
                  id="resetEmail"
                  type="email"
                  value={resetEmail}
                  onChange={(e) => setResetEmail(e.target.value)}
                  placeholder="email@esempio.com"
                  required
                  disabled={resetLoading}
                  className="mt-1"
                  data-testid="forgot-password-email-input"
                />
              </div>

              {resetError && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm" data-testid="forgot-password-error-alert">
                  {resetError}
                </div>
              )}

              <div className="flex gap-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setShowForgotPassword(false);
                    setResetEmail('');
                    setResetError('');
                  }}
                  disabled={resetLoading}
                  className="flex-1"
                  data-testid="forgot-password-cancel-button"
                >
                  Annulla
                </Button>
                <Button
                  type="submit"
                  disabled={resetLoading}
                  className="flex-1 bg-blue-600 hover:bg-blue-700"
                  data-testid="forgot-password-submit-button"
                >
                  {resetLoading ? 'Invio...' : 'Invia Link'}
                </Button>
              </div>
            </form>
          ) : (
            <div className="py-4">
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4" data-testid="forgot-password-success-alert">
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <div className="text-sm text-green-800">
                    <p className="font-medium mb-1">Email inviata!</p>
                    <p>
                      Se l'email è registrata, riceverai un link per reimpostare la password entro pochi minuti.
                    </p>
                  </div>
                </div>
              </div>
              
              <p className="text-xs text-slate-500 text-center">
                Controlla anche la cartella spam se non vedi l'email.
              </p>
            </div>
          )}
        </DialogContent>
      </Dialog>
      <a href={isHomeSmart ? '/home-smart/privacy' : '/privacy'} data-testid="login-privacy-link"
        className="fixed bottom-3 left-1/2 -translate-x-1/2 text-xs text-slate-600 hover:text-slate-800 underline">
        Privacy Policy
      </a>
    </div>
  );
};

export default LoginPage;
